import type React from 'react';
import { ProductCard } from '../atoms/ProductCard';
import LoadingSpinner from '../atoms/LoadingSpinner';
import Button from '../atoms/Button';
import EmptyState from '../molecules/EmptyState';
import ErrorState from '../molecules/ErrorState';
import { type PaginatedResponse, type ProductListItem } from '../../types/products';

interface ProductsListProps {
  data?: PaginatedResponse<ProductListItem>;
  isLoading: boolean;
  error: Error | null;
  page: number;
  onPageChange: (page: number) => void;
  onProductClick?: (productId: string) => void;
}

export const ProductsList: React.FC<ProductsListProps> = ({
  data,
  isLoading,
  error,
  page,
  onPageChange,
  onProductClick
}) => {
  if (isLoading && !data) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <ErrorState
        message={error.message ?? 'Failed to load products. Please try again.'}
      />
    );
  }

  if (!data?.data || data.data.length === 0) {
    return (
      <EmptyState
        title="No products found"
        description="Try adjusting your filters or search criteria."
      />
    );
  }

  const { data: products, pagination } = data;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {products.map((product) => (
          <ProductCard
            key={product.id}
            id={product.id}
            name={product.name}
            description={product.description}
            imageUrl={product.imageUrl}
            sku={product.sku}
            categories={product.categories}
            isActive={product.isActive}
            onClick={onProductClick}
            price={product.price}
            variants={product.variants}
          />
        ))}
      </div>

      {pagination.totalPages > 1 && (
        <div className="flex justify-center items-center space-x-4 mt-8">
          <Button
            variant="secondary"
            size="small"
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1}
          >
            Previous
          </Button>
          
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600">
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <span className="text-sm text-gray-500">
              ({pagination.total} total products)
            </span>
          </div>

          <Button
            variant="secondary"
            size="small"
            onClick={() => onPageChange(page + 1)}
            disabled={page >= pagination.totalPages}
          >
            Next
          </Button>
        </div>
      )}
      
      {isLoading && data && (
        <div className="flex justify-center py-4">
          <LoadingSpinner size="sm" />
        </div>
      )}
    </div>
  );
};